import objectHash from 'object-hash';
import React, {useEffect, useRef, useMemo} from 'react';
import {fontsStoreActions} from '@contentlab/stores/fontStore';
import {
  StyleConfig,
  defaultStyle,
  FontVariants,
  Alignment,
  AnimationKind,
  Animation,
  WordSelectionStrategy,
  Word,
  BackgroundAnimationKind,
  Easing,
  Background,
  CaptionsData,
  LineLayout,
  CaptionLayout,
} from '@contentlab/types/captions';
import {FALLBACK_FONT_FAMILIES} from '@contentlab/views/contentlab/pages/captions-page/components/style-editor/SharedConstants';
import {
  DEFAULT_ANIMATION_DURATION,
  FADE_START_OPACITY,
  SLIDE_IN_OFFSET,
} from './animation/defaults';
import applyEasing from './animation/easing';
import {
  findCurrentAndPreviousWords,
  calculateWordBoxes,
  calculateWordPrintOffsets,
  interpolateBox,
  calculateHighlightSmoothTransitionProgress,
  drawInterpolatedBackground,
  drawWordHighlightSmooth,
  drawWordHighlightBox,
} from './animation/karaoke-highlight';
import {
  PERFORMANCE,
  LAYOUT,
  TYPOGRAPHY,
  BACKGROUND,
  KARAOKE,
  calculateScaleFactor,
} from './config/CaptionRenderingConfig';
import {TextProcessing} from './text-processing';

interface VideoCaptionOverlayStandaloneProps {
  testID?: string;
  captionsData: CaptionsData;
  style?: StyleConfig;
  getCurrentTime: () => number;
  animationRequired: boolean;
  canvasWidth: number;
  canvasHeight: number;
}

// Helper function to get CSS properties for the font variant
// Convert variant to CSS font descriptor parts

const getVariantCSSProperties = (
  variant: FontVariants | string | undefined
): string => {
  switch (variant) {
    case FontVariants.BOLD:
    case 'bold':
      return 'bold';
    case FontVariants.ITALIC:
    case 'italic':
      return 'italic';
    case FontVariants.REGULAR:
    case 'normal':
    default:
      return 'normal';
  }
};

function getFontSize(style: StyleConfig, scaleFactor: number) {
  const baseFontSize =
    style.typography?.fontSettings?.size || TYPOGRAPHY.DEFAULT_FONT_SIZE;
  return baseFontSize * scaleFactor;
}

function setupFont(
  context: CanvasRenderingContext2D,
  style: StyleConfig,
  scaleFactor: number
): {fontSize: number; fontFamily: string; fontColor: string} {
  const fontSize = getFontSize(style, scaleFactor);

  const selectedFont = style.typography?.fontSettings?.selectedFont;
  const fontId = selectedFont?.fontId || FALLBACK_FONT_FAMILIES[0].id;
  const fontVariant = selectedFont?.variant || FontVariants.REGULAR;

  const fontColor = style.typography?.textColor?.hex ?? '#ffffff';

  // Get the font family and variant CSS properties
  const fontMetadata = fontsStoreActions.getFontMetadata(fontId);
  const fontFamily =
    selectedFont?.fontFamily ||
    fontMetadata?.name ||
    fontId ||
    FALLBACK_FONT_FAMILIES[0].name;
  const variantCSS = getVariantCSSProperties(fontVariant);
  // Let browser handle font matching with the loaded font faces
  const fontString = `${variantCSS} ${fontSize}px "${fontFamily}"`;
  // Set font with the specific variant properties
  context.font = fontString;
  context.textAlign = style.typography?.textAlignment || 'center';
  context.textBaseline = 'middle';
  return {fontSize, fontFamily, fontColor};
}

function getPosition(
  style: StyleConfig,
  canvasWidth: number,
  canvasHeight: number,
  lines: string[],
  fontSize: number
): {x: number; lineHeight: number; startY: number} {
  const padding = canvasWidth * ((1 - LAYOUT.MAX_TEXT_WIDTH_RATIO) / 2);
  let x = 0;
  if (style.typography?.textAlignment === Alignment.LEFT) {
    x = padding;
  } else if (style.typography?.textAlignment === Alignment.RIGHT) {
    x = canvasWidth - padding;
  } else {
    x = canvasWidth * 0.5;
  }
  let lineHeight =
    fontSize *
    (style.typography.spacing?.lineHeight ||
      LAYOUT.DEFAULT_LINE_HEIGHT_MULTIPLIER);
  if (
    style.animation?.backgroundAnimationType === BackgroundAnimationKind.BOX
  ) {
    lineHeight *= style.animation?.wordMaxScaling ?? 1;
  }
  const totalHeight = lines.length * lineHeight;

  // Use normalized vertical position (0.0 = top, 1.0 = bottom)
  const normalizedPosition = style.normalizedVerticalPosition ?? 1.0; // Default to bottom
  const baseY = canvasHeight * normalizedPosition;

  // Adjust for multiple lines and ensure text stays within bounds
  let startY = baseY - totalHeight / 2 + lineHeight / 2;

  const verticalPadding = canvasHeight * LAYOUT.CANVAS_EDGE_PADDING_RATIO;
  if (startY - fontSize / 2 < verticalPadding) {
    startY = verticalPadding + fontSize / 2;
  }

  const lastLineY = startY + (lines.length - 1) * lineHeight + fontSize / 2;
  if (lastLineY > canvasHeight - padding) {
    startY -= lastLineY - (canvasHeight - padding);
  }
  return {x, lineHeight, startY};
}

function drawBackground(
  context: CanvasRenderingContext2D,
  background: Background,
  lines: string[],
  fontSize: number,
  position: {x: number; lineHeight: number; startY: number},
  width?: number
): void {
  context.save();

  // Create an offscreen canvas to draw backgrounds at full opacity
  const offscreenCanvas = document.createElement('canvas');
  offscreenCanvas.width = context.canvas.width;
  offscreenCanvas.height = context.canvas.height;
  const offscreenContext = offscreenCanvas.getContext('2d');

  if (!offscreenContext) {
    console.error('Offscreen context not found');
    return;
  }

  offscreenContext.fillStyle = background.color?.hex ?? '#000000';

  lines.forEach((line, index) => {
    const y = position.startY + index * position.lineHeight;
    const metrics = context.measureText(line);
    const textWidth = width ?? metrics.width;
    const boxPadding = background.horizontalPadding
      ? background.horizontalPadding * fontSize
      : fontSize * BACKGROUND.BOX_PADDING_TO_FONT_RATIO;

    const boxHeight = background.verticalPadding
      ? (background.verticalPadding + 1) * fontSize
      : fontSize * BACKGROUND.BOX_HEIGHT_TO_FONT_RATIO;
    const boxY = y - boxHeight / 2;

    let boxX;

    if (context.textAlign === Alignment.CENTER) {
      boxX = position.x - textWidth / 2 - boxPadding;
    } else if (context.textAlign === Alignment.LEFT) {
      boxX = position.x - boxPadding;
    } else {
      boxX = position.x - textWidth - boxPadding;
    }
    const boxWidth = textWidth + boxPadding * 2;

    offscreenContext.beginPath();
    offscreenContext.roundRect(
      boxX,
      boxY,
      boxWidth,
      boxHeight,
      background.cornerRadius ?? 0
    );
    offscreenContext.fill();
  });

  // Draw the offscreen canvas onto the main canvas with the desired opacity
  context.globalAlpha = background.color.opacity;
  context.drawImage(offscreenCanvas, 0, 0);

  context.restore();
}
function drawStroke(
  context: CanvasRenderingContext2D,
  strokeColor: string,
  strokeWidth: number,
  line: string,
  x: number,
  y: number
): void {
  context.strokeStyle = strokeColor;
  context.lineWidth = strokeWidth;
  context.lineJoin = 'round';
  context.strokeText(line, x, y);
}
function drawDropShadow(
  context: CanvasRenderingContext2D,
  style: StyleConfig,
  fontColor: string,
  x: number,
  y: number,
  line: string,
  strokeWidth?: number,
  scaleFactor?: number
): void {
  if (!style.styling.dropShadow) {
    return;
  }
  context.save();
  const scale = scaleFactor ?? 1;
  // Scale offset by half the font scale factor since font grows in both directions
  const offsetScale = scale / 2;
  // Blur can use full scale factor since it's an absolute effect
  context.shadowBlur = (style.styling.dropShadow?.blur ?? 10) * scale;
  context.shadowColor = style.styling.dropShadow?.color.hex ?? '#000000';
  context.shadowOffsetX =
    (style.styling.dropShadow?.offsetX ?? 2) * offsetScale;
  context.shadowOffsetY =
    (style.styling.dropShadow?.offsetY ?? 2) * offsetScale;
  if (style.styling.textStroke?.color.hex !== 'transparent') {
    drawStroke(
      context,
      style.styling.textStroke?.color.hex ?? 'transparent',
      strokeWidth ?? style.styling.textStroke?.width ?? 0,
      line,
      x,
      y
    );
  }
  context.fillStyle = fontColor;
  context.fillText(line, x, y);
  context.restore();
}

function drawText(
  lines: string[],
  position: {x: number; lineHeight: number; startY: number},
  context: CanvasRenderingContext2D,
  fontColor: string,
  strokeColor: string,
  strokeWidth: number,
  style: StyleConfig,
  scaleFactor: number
): void {
  lines.forEach((line, index) => {
    const y = position.startY + index * position.lineHeight;

    if (style.styling.dropShadow) {
      drawDropShadow(
        context,
        style,
        fontColor,
        position.x,
        y,
        line,
        strokeWidth,
        scaleFactor
      );
    }

    // Draw text with stroke if enabled
    if (strokeWidth > 0 && strokeColor !== 'transparent') {
      drawStroke(context, strokeColor, strokeWidth, line, position.x, y);
    }

    // Draw text fill
    context.fillStyle = fontColor;

    context.fillText(line, position.x, y);
  });
}

function isKaraokeHighlightOn(animation: Animation | null | undefined) {
  return (
    animation?.type === AnimationKind.KARAOKE_HIGHLIGHT && animation?.wordColor
  );
}

function isKaraokeBackgroundOn(animation: Animation | null | undefined) {
  return (
    animation?.type === AnimationKind.KARAOKE_HIGHLIGHT && animation?.background
  );
}

function getHighlightFillColor(
  fontColorHex: string,
  currentTime: number,
  word: Word,
  animation: Animation
) {
  const highlightColor = isKaraokeHighlightOn(animation)
    ? (animation.wordColor?.hex ?? KARAOKE.DEFAULT_WORD_COLOR.hex)
    : (animation.wordColor?.hex ?? fontColorHex);

  const selectionStrategy = isKaraokeHighlightOn(animation)
    ? (animation.wordSelectionStrategy ??
      KARAOKE.DEFAULT_WORD_SELECTION_STRATEGY)
    : animation.wordSelectionStrategy;

  switch (selectionStrategy) {
    case WordSelectionStrategy.FILL:
      if (currentTime >= word.start) {
        return highlightColor;
      }
      return fontColorHex;
    case WordSelectionStrategy.PULSE:
      return currentTime >= word.start && currentTime < word.end
        ? highlightColor
        : fontColorHex;
    default:
      return fontColorHex;
  }
}

function isCurrentWord(word: Word, currentTime: number) {
  return currentTime >= word.start && currentTime < word.end;
}

function getFirstWordPosition(
  x: number,
  lineLength: number,
  alignment: Alignment
) {
  switch (alignment) {
    case Alignment.CENTER:
      return x - lineLength / 2;
    case Alignment.LEFT:
      return x;
    case Alignment.RIGHT:
      return x - lineLength;
    default:
      return x;
  }
}

function drawHighlightSmoothText(
  lines: string[],
  position: {x: number; lineHeight: number; startY: number},
  context: CanvasRenderingContext2D,
  fontColor: string,
  fontSize: number,
  strokeColor: string,
  strokeWidth: number,
  style: StyleConfig,
  wordTimings: Word[],
  currentTime: number,
  scaleFactor: number,
  captionLayout: CaptionLayout
): void {
  const {animation} = style;
  if (!animation) return;

  const textAlignment = style.typography?.textAlignment ?? Alignment.CENTER;
  const {current: currentWordInfo, previous: previousWordInfo} =
    findCurrentAndPreviousWords(wordTimings, currentTime, isCurrentWord);

  const wordBoxes = calculateWordBoxes(
    lines,
    wordTimings,
    position,
    context,
    textAlignment,
    getFirstWordPosition,
    calculateWordPrintOffsets,
    captionLayout
  );

  const animationBackground = isKaraokeBackgroundOn(animation)
    ? {
        ...KARAOKE.DEFAULT_BACKGROUND,
        ...animation.background,
      }
    : animation.background;

  if (animationBackground && (currentWordInfo || previousWordInfo)) {
    context.save();
    const progress = currentWordInfo
      ? calculateHighlightSmoothTransitionProgress(
          currentWordInfo.word,
          currentTime
        )
      : 1; // Full progress when staying at previous word

    const animationWithUpdatedBackground = {
      ...animation,
      background: animationBackground,
    };

    drawInterpolatedBackground(
      context,
      currentWordInfo,
      previousWordInfo,
      wordBoxes,
      progress,
      animationWithUpdatedBackground,
      style,
      fontSize,
      position,
      interpolateBox,
      drawBackground
    );
    context.restore();
  }

  // Draw all text
  let globalWordIndex = 0;
  lines.forEach((_, lineIndex) => {
    const {wordCount, wordOrder, lineWidth, wordWidths} =
      captionLayout.lineLayouts[lineIndex];
    const y = position.startY + lineIndex * position.lineHeight;
    let x = getFirstWordPosition(position.x, lineWidth, textAlignment);

    for (let i = 0; i < wordCount; i += 1) {
      const localWordIndex = wordOrder[i];
      const word = wordTimings[globalWordIndex + localWordIndex];
      if (word) {
        const wordWidth = wordWidths[localWordIndex];
        const {preDelta, postDelta} = calculateWordPrintOffsets(
          wordWidth,
          textAlignment
        );

        x += preDelta;

        drawWordHighlightSmooth(
          {
            context,
            word,
            x,
            y,
            fontColor,
            strokeColor,
            strokeWidth,
            style,
            animation,
            currentTime,
          },
          getHighlightFillColor,
          (ctx, styleConfig, color, xPos, yPos, text, strokeW) =>
            drawDropShadow(
              ctx,
              styleConfig,
              color,
              xPos,
              yPos,
              text,
              strokeW,
              scaleFactor
            ),
          drawStroke
        );

        x += postDelta + captionLayout.spaceWidth;
      }
    }
    globalWordIndex += wordCount;
  });
}

function drawHighlightBoxText(
  lines: string[],
  position: {x: number; lineHeight: number; startY: number},
  context: CanvasRenderingContext2D,
  fontColor: string,
  fontSize: number,
  strokeColor: string,
  strokeWidth: number,
  style: StyleConfig,
  wordTimings: Word[],
  currentTime: number,
  scaleFactor: number,
  captionLayout: CaptionLayout
): void {
  const {animation} = style;
  if (!animation) {
    return;
  }

  const textAlignment = style.typography?.textAlignment ?? Alignment.CENTER;
  let globalWordIndex = 0;

  lines.forEach((_, lineIndex) => {
    const {wordCount, wordOrder, lineWidth, wordWidths} =
      captionLayout.lineLayouts[lineIndex];
    const y = position.startY + lineIndex * position.lineHeight;
    let x = getFirstWordPosition(position.x, lineWidth, textAlignment);

    for (let i = 0; i < wordCount; i += 1) {
      const wordIndex = wordOrder[i];
      const word = wordTimings[globalWordIndex + wordIndex];
      if (word) {
        const wordWidth = wordWidths[wordIndex];
        const {preDelta, postDelta} = calculateWordPrintOffsets(
          wordWidth,
          textAlignment
        );

        x += preDelta;

        drawWordHighlightBox(
          {
            word,
            x,
            y,
            context,
            fontColor,
            fontSize,
            strokeColor,
            strokeWidth,
            style,
            currentTime,
            animation,
            lineHeight: position.lineHeight,
          },
          isCurrentWord,
          getHighlightFillColor,
          drawBackground,
          (ctx, styleConfig, color, xPos, yPos, text, strokeW) =>
            drawDropShadow(
              ctx,
              styleConfig,
              color,
              xPos,
              yPos,
              text,
              strokeW,
              scaleFactor
            ),
          drawStroke
        );

        x += postDelta + captionLayout.spaceWidth;
      }
    }
    globalWordIndex += wordCount;
  });
}

function animateTextFadeIn(
  context: CanvasRenderingContext2D,
  progress: number
) {
  context.globalAlpha =
    FADE_START_OPACITY + (1 - FADE_START_OPACITY) * progress;
}

function animateTextPopIn(
  context: CanvasRenderingContext2D,
  animation: Animation,
  progress: number,
  textPosition: {x: number; totalHeight: number; startY: number}
) {
  // Animation parameters
  const startScale = 0.8;
  const peakScale = animation.wordMaxScaling ?? 1.2;
  const finalScale = 1.0;
  const peakProgress = 0.5;

  let scale;
  if (progress < peakProgress) {
    // Phase 1: Linear growth from small to big
    const phase1Progress = progress / peakProgress;
    scale = startScale + (peakScale - startScale) * phase1Progress;
  } else {
    // Phase 2: Linear shrink from big to normal
    const phase2Progress = (progress - peakProgress) / (1 - peakProgress);
    scale = peakScale - (peakScale - finalScale) * phase2Progress;
  }

  // Apply scaling from center
  context.translate(
    textPosition.x,
    textPosition.startY + textPosition.totalHeight / 2
  );

  context.scale(scale, scale);

  context.translate(
    -textPosition.x,
    -(textPosition.startY + textPosition.totalHeight / 2)
  );
}
function animateTextSlideOut(
  context: CanvasRenderingContext2D,
  progress: number
) {
  const yOffset = (1 - progress) * SLIDE_IN_OFFSET;

  context.translate(0, yOffset);
}
function animateTextEnterence(
  context: CanvasRenderingContext2D,
  styleConfig: StyleConfig,
  currentTime: number,
  start: number,
  textPosition: {x: number; totalHeight: number; startY: number}
) {
  const {animation} = styleConfig;
  if (!animation) {
    return;
  }
  const elapsed = currentTime - start;
  const duration = animation.duration ?? DEFAULT_ANIMATION_DURATION;
  let progress = duration > 0 ? elapsed / duration : 1;
  progress = Math.min(Math.max(progress, 0), 1);
  progress = applyEasing(progress, animation.easing ?? Easing.LINEAR);

  switch (animation?.type) {
    case AnimationKind.FADE_IN:
      animateTextFadeIn(context, progress);
      break;
    case AnimationKind.POP_IN:
      animateTextPopIn(context, animation, progress, textPosition);
      break;
    case AnimationKind.SLIDE_IN:
      animateTextFadeIn(context, progress);
      animateTextSlideOut(context, progress);
      break;
    default:
      break;
  }
}

function isRTLLang(languageCode: string) {
  try {
    const locale = new Intl.Locale(languageCode);
    // Checks if getTextInfo is available:
    if ('getTextInfo' in locale) {
      const textInfo = (locale as any).getTextInfo();
      return textInfo.direction === 'rtl';
    }
  } catch {
    // Fallback - default to LTR
  }
  return false;
}

export function getWordOrder(count: number, isRTL: boolean) {
  return isRTL
    ? Array.from({length: count}, (_, i) => count - 1 - i)
    : Array.from({length: count}, (_, i) => i);
}

function VideoCaptionOverlayStandalone({
  testID = 'video-caption-overlay',
  captionsData,
  style = defaultStyle,
  getCurrentTime,
  animationRequired,
  canvasWidth,
  canvasHeight,
}: VideoCaptionOverlayStandaloneProps) {
  let currentTime = getCurrentTime();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const isRTLLanguage = useMemo(
    () => isRTLLang(captionsData.languageCode),
    [captionsData.languageCode]
  );

  const layoutCacheRef = useRef<Map<string, CaptionLayout>>(new Map());

  function getCaptionLayout(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    textAlign: Alignment | undefined,
    isRTL: boolean
  ): CaptionLayout {
    const fontKey = ctx.font;
    const hash = objectHash({fontKey, textAlign, isRTL, lines});
    const cached = layoutCacheRef.current.get(hash);
    if (cached) return cached;

    const spaceWidth = ctx.measureText(' ').width;

    const lineLayouts: LineLayout[] = lines.map((line) => {
      const words = line.split(' '); // keep in sync with timedWords tokenization
      const wordCount = words.length;
      const wordOrder = getWordOrder(wordCount, isRTL);
      const lineWidth = ctx.measureText(line).width;

      // Calculate and cache word widths
      const wordWidths = words.map((word) => ctx.measureText(word).width);

      return {wordCount, wordOrder, lineWidth, wordWidths};
    });

    const captionLayout: CaptionLayout = {lineLayouts, spaceWidth};

    if (layoutCacheRef.current.size >= PERFORMANCE.MAX_CAPTION_LAYOUTS) {
      const oldestKey = layoutCacheRef.current.keys().next().value;
      if (oldestKey) layoutCacheRef.current.delete(oldestKey);
    }

    layoutCacheRef.current.set(hash, captionLayout);
    return captionLayout;
  }

  const drawCaption = (
    context: CanvasRenderingContext2D,
    styleConfig: StyleConfig,
    renderWidth: number,
    renderHeight: number
  ) => {
    // Scale font size based on resolution - use smaller dimension for consistent scaling
    const scaleFactor = calculateScaleFactor(renderWidth, renderHeight);
    const {fontColor, fontSize} = setupFont(context, styleConfig, scaleFactor);
    const {lines, start} = TextProcessing.getCurrentLines(
      context,
      captionsData.captions,
      styleConfig,
      renderWidth * LAYOUT.MAX_TEXT_WIDTH_RATIO,
      currentTime
    );
    const strokeColor =
      styleConfig.styling.textStroke?.color.hex ?? 'transparent';
    const strokeWidth =
      (styleConfig.styling.textStroke?.width ?? 0) * scaleFactor;

    const position = getPosition(
      styleConfig,
      renderWidth,
      renderHeight,
      lines,
      fontSize
    );

    if (styleConfig.styling.background?.color) {
      drawBackground(
        context,
        styleConfig.styling.background,
        lines,
        fontSize,
        position
      );
    }
    context.save();
    animateTextEnterence(context, styleConfig, currentTime, start, {
      x: position.x,
      totalHeight: position.lineHeight * lines.length,
      startY: position.startY,
    });
    drawText(
      lines,
      position,
      context,
      fontColor,
      strokeColor,
      strokeWidth,
      styleConfig,
      scaleFactor
    );
    context.restore();
  };

  const drawHighlightCaption = (
    context: CanvasRenderingContext2D,
    styleConfig: StyleConfig,
    renderWidth: number,
    renderHeight: number
  ) => {
    // Scale font size based on resolution - use smaller dimension for consistent scaling
    const scaleFactor = calculateScaleFactor(renderWidth, renderHeight);

    const {fontColor, fontSize} = setupFont(context, styleConfig, scaleFactor);
    const {lines, timedWords} = TextProcessing.getHighlightText(
      context,
      captionsData.captions,
      styleConfig,
      renderWidth * LAYOUT.MAX_TEXT_WIDTH_RATIO,
      currentTime
    );

    if (lines.length === 0 || !timedWords || timedWords.length === 0) {
      return;
    }
    const strokeColor =
      styleConfig.styling.textStroke?.color.hex || 'transparent';
    const strokeWidth =
      (styleConfig.styling.textStroke?.width ?? 0) * scaleFactor;

    const position = getPosition(
      styleConfig,
      renderWidth,
      renderHeight,
      lines,
      fontSize
    );

    context.save();

    const backgroundAnimationType = isKaraokeBackgroundOn(styleConfig.animation)
      ? (styleConfig.animation?.backgroundAnimationType ??
        KARAOKE.DEFAULT_BACKGROUND_ANIMATION_TYPE)
      : styleConfig.animation?.backgroundAnimationType;

    const captionLayout = getCaptionLayout(
      context,
      lines,
      styleConfig.typography?.textAlignment,
      isRTLLanguage
    );

    if (backgroundAnimationType === BackgroundAnimationKind.SMOOTH) {
      drawHighlightSmoothText(
        lines,
        position,
        context,
        fontColor,
        fontSize,
        strokeColor,
        strokeWidth,
        styleConfig,
        timedWords,
        currentTime,
        scaleFactor,
        captionLayout
      );
    } else {
      drawHighlightBoxText(
        lines,
        position,
        context,
        fontColor,
        fontSize,
        strokeColor,
        strokeWidth,
        styleConfig,
        timedWords,
        currentTime,
        scaleFactor,
        captionLayout
      );
    }

    if (styleConfig.styling.background?.color) {
      context.globalCompositeOperation = 'destination-over';
      drawBackground(
        context,
        styleConfig.styling.background,
        lines,
        fontSize,
        position
      );
      context.globalCompositeOperation = 'source-over';
    }

    context.restore();
  };

  const render = () => {
    if (!style) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);

    context.save();
    if (style.animation?.type === AnimationKind.KARAOKE_HIGHLIGHT) {
      drawHighlightCaption(context, style, canvas.width, canvas.height);
    } else {
      drawCaption(context, style, canvas.width, canvas.height);
    }

    context.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const frameInterval = 1000 / PERFORMANCE.FPS_LIMIT;
    let animationId: number;
    let lastFrameTime = 0;

    const animate = (currentFrameTime: number) => {
      const deltaTime = currentFrameTime - lastFrameTime;

      if (deltaTime >= frameInterval) {
        currentTime = getCurrentTime();
        render();
        lastFrameTime = currentFrameTime;
      }

      if (animationRequired) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animate(performance.now());

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [canvasWidth, canvasHeight, getCurrentTime, style, captionsData.captions]);

  return (
    <canvas
      ref={canvasRef}
      data-testid={testID}
      dir={isRTLLanguage ? 'rtl' : 'ltr'}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        letterSpacing: `${style?.typography?.spacing?.kerning ?? 0}px`,
      }}
    />
  );
}

export default VideoCaptionOverlayStandalone;
